import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type {
  SearchCompanyHit,
  SearchCompanyResult,
  SearchUserHit,
  SearchUserResult,
} from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';

import { ContactTypeDropdown } from './components/dropdown/contactTypeDropdown';
import { ContactPreview } from './components/preview/contactPreview';
import { ContactResult } from './components/result/contactResult';

type ContactsResultsProps<TScope extends 'users' | 'companies' = 'users'> = {
  scope: TScope;
  onChangeScope: (scope: 'users' | 'companies') => void;
} & (TScope extends 'users'
  ? { result: SearchUserResult }
  : { result: SearchCompanyResult });

/**
 * Represents a component that is used to render the contacts tab of the search
 * results
 */
export const ContactsResults = <
  TScope extends 'users' | 'companies' = 'users',
>({
  scope,
  result,
  onChangeScope,
}: ContactsResultsProps<TScope>): JSX.Element => {
  const contactType = scope === 'companies' ? 'company' : 'user';

  const [previewedHit, setPreviewedHit] = useState<
    SearchUserHit | SearchCompanyHit | null
  >(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedHit,
  });

  const handleNavigateToContact = useCallback(() => {
    ampli.searchContactPreviewGoToClicked({ type: contactType });
  }, [contactType]);

  const handleSelectHit = useCallback(
    (hit: SearchUserHit | SearchCompanyHit) => {
      if (hit.id === previewedHit?.id) return;

      setPreviewedHit(hit);
      ampli.searchContactPreviewOpened({ type: contactType });
    },
    [contactType, previewedHit, setPreviewedHit],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedHit(null);
    ampli.searchContactPreviewClosed({ type: contactType });
  }, [contactType, setPreviewedHit]);

  const handleChangeContactType = useCallback(
    (type: (SearchUserResult | SearchCompanyResult)['hits_type']) => {
      setPreviewedHit(null);
      ampli.searchContactTypeChanged({ type });

      const newScope = type === 'company' ? 'companies' : 'users';
      onChangeScope(newScope);
    },
    [onChangeScope],
  );

  const noResults = !result?.nb_hits;
  if (noResults) {
    return <SearchResultsEmpty />;
  }

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom>
          <Column.Slot placement="start">
            <ContactTypeDropdown
              selected={contactType}
              onChange={handleChangeContactType}
            />
          </Column.Slot>
        </Column.Row>

        <ContactResult
          result={result}
          selectedId={previewedHit?.id}
          onSelectHit={handleSelectHit}
        />
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedHit ? (
            <ContactPreview
              hit={previewedHit}
              onGoToContact={handleNavigateToContact}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a contact"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of the contact you&apos;re looking
                      for? <br /> Go ahead and click a contact to see it here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
